/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {TableLineModel} from '../models/table-line.model';
import {
    merge,
    Observable,
    pipe,
    Subscription,
    timer
} from 'rxjs';
import {TableLineDataModel} from '../models/table-line-data.model';
import {KeyValue} from '@angular/common';
import {
    filter,
    map
} from 'rxjs/operators';
import {TableService} from '../table-service/table.service';
import TableLineConfiguration from './configuration/table-line.configuration';
import {TableAvailableActionsEnum} from '../models/table-available-actions.enum';

@Component({
    selector: 'app-table-line',
    templateUrl: './table-line.component.html',
    styleUrls: ['./../table-global-styles.css', './table-line.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableLineComponent<Enum, DataObject> implements OnChanges {
    /**
     * The {@link TableLineModel} object
     */
    @Input() line: TableLineModel<Enum, DataObject>;
    /**
     * The detail string to display in the full line tooltip
     */
    @Input() fullLineTooltipDetail: string;
    /**
     * If the line is expandable, i.e. has an accordion
     */
    @Input() expandableLine: boolean;
    /**
     * The columns template class to be applied to the grid component
     */
    @Input() columnStyles: { [key: string]: any };
    /**
     * The data pipes map for each entry (if they exist) of the table line
     */
    @Input() dataPipes: Map<Enum, (value: any) => string | number>;
    /**
     * The hasDot map for each entry (if they exist) of the hasDot attribute
     */
    @Input() hasDot: Map<Enum, boolean>;
    /**
     * The hasTextAlignRight map for each entry (if they exist) of the hasTextAlignRight attribute
     */
    @Input() hasTextAlignRight: Map<Enum, boolean>;
    /**
     * The hasFloatContent map for each entry (if they exist) of the hasFloatContent attribute
     */
    @Input() hasFloatContent: Map<Enum, boolean>;
    /**
     * The hasCopyPasteContent map for each entry (if they exist) of the hasCopyPasteContent attribute
     */
    @Input() hasCopyPasteButton: Map<Enum, boolean>;
    /**
     * If the respective line accordion is open, i.e. line was selected
     */
    @Input() lineIsSelected = false;
    /**
     * If the respective line has animation
     */
    @Input() hasAnimation = false;
    /**
     * If the respective line has mouse hover
     */
    @Input() hasMouseHover = false;
    /**
     * If the respective line has highlight hover
     */
    @Input() hasHighlight = false;
    /**
     * The {@link TemplateRef} to show in the line accordion
     */
    @Input() accordionLineTemplate: TemplateRef<any>;
    /**
     * Input event emitter to register scroll events
     */
    @Input() scrollEvent = new EventEmitter<void>();
    /**
     * If the accordion behaviour should be disabled
     */
    @Input() disableAccordion = false;
    /**
     * If the accordion line is to be close
     */
    @Input() isToCloseAccordion = false;
    /**
     * If the line is blurred
     */
    @Input() isBlurred: boolean;
    /**
     * Table line available actions EDIT, DELETE, VIEW
     */
    @Input() lineAvailableActions: TableAvailableActionsEnum[] = [];
    @Input() excludedColumns: Enum[] = [];
    /**
     * Event emitter for when the mouse is hover line
     */
    @Output() isMouseHoverLine = new EventEmitter<boolean>();
    /**
     * Event emitter for when the mouse is leave line
     */
    @Output() isMouseLeaveLine = new EventEmitter<boolean>();
    /**
     * Event emitter for when the accordion status changes
     */
    @Output() accordionStatusChange = new EventEmitter<boolean>();
    /**
     * Line {@link ElementRef} for scroll/line height management
     */
    @ViewChild('lineElement') lineElement: ElementRef;
    /**
     * Line accordion {@link ElementRef}
     */
    @ViewChild('accordionContainer') accordionContainer: ElementRef;
    /**
     * Emitter to manually close the full line tooltip
     */
    public closeFullLineTooltipEmitter = new EventEmitter<void>();
    /**
     * Emitter to manually open the full line tooltip
     */
    public openFullLineTooltipEmitter = new EventEmitter<void>();
    /**
     * Line as header style class
     */
    public lineAsHeaderClass = 'table-line';
    public applyMouseHover: boolean;
    public hasLineMouseHoverAndAvailableActions = false;
    TableAvailableActionsEnum = TableAvailableActionsEnum;
    readonly backgroundColorHover = '#EFEFEF';
    /**
     * Subscriptions variable
     */
    private subscription: Subscription = new Subscription();

    constructor(private tableService: TableService<Enum, DataObject>) {
    }

    get lineAccordionPadding(): { [key: string]: string } {
        return {
            'padding-bottom': `${TableLineConfiguration.tableLinePaddingBottom}px`
        };
    }

    /**
     * Returns the accordion button (arrow) class
     */
    get accordionButtonClass(): string {
        let buttonClass = 'table-line-expand-button';
        if (this.expandableLine) {
            if (this.lineIsSelected) {
                buttonClass += ' iwp-icon-gen_arrow_down opened';
            } else {
                buttonClass += ' iwp-icon-gen_arrow_right';
                if (this.disableAccordion) {
                    buttonClass += ' disabled';
                }
            }
        }

        return buttonClass;
    }

    /**
     * Returns the table row class
     */
    get tableRowClass(): string {
        let finalClass = 'table-row';
        if (this.lineIsSelected) {
            finalClass += ' opened';
        } else if (this.isBlurred) {
            finalClass += ' disabled';
        } else if (this.hasMouseHover) {
            finalClass += ' isHoverLine';
        }
        return finalClass;
    }

    get tableRowGridStyle(): { [key: string]: string } {
        return !this.expandableLine && {'grid-template-columns': 'auto'};
    }

    /**
     * Return the full line tooltip text
     * <p> Add the {@link fullLineTooltipDetail} to the "Retract..." or "Expand..." according to the line status
     */
    get fullLineTooltipText(): string {
        if (this.line.isOpen) {
            return `Retract ${this.fullLineTooltipDetail}`;
        } else {
            return `Expand ${this.fullLineTooltipDetail}`;
        }
    }

    get isBlurredClass(): string {
        if (this.isBlurred) {
            return 'disabled';
        }
    }

    get animationCssClass(): string {
        return this.hasAnimation ? 'has-animation' : '';
    }

    get tableLineClass(): string {
        return `${this.lineAsHeaderClass} ${this.isBlurredClass}`;
    }

    get retrieveFloatContent(): boolean {
        return this.hasFloatContent?.size > 0;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['hasAnimation']?.currentValue !== changes['hasAnimation']?.previousValue
                && changes['hasAnimation']?.currentValue) {
            timer(1)
                    .subscribe(() => {
                        (this.lineElement?.nativeElement as HTMLElement)?.scrollIntoView();
                    });
        }
    }

    /**
     * Return the color of the cell dot
     */
    getDotClass(dotClass: string): string {
        if (!this.isBlurred && this.line.isOpen) {
            return dotClass + ' opened';
        } else {
            return dotClass;
        }
    }

    /**
     * Function for the ngFor to keep the original order in each the items were added to the {@link TableLineModel} data map
     */
    originalOrder(a: KeyValue<string, TableLineDataModel>, b: KeyValue<string, TableLineDataModel>): number {
        return 0;
    }

    /**
     * Return if a specific attribute has a data pipe in the {@link dataPipes} map
     * @param attribute the attribute type to check
     */
    attributeHasPipe(attribute: Enum): boolean {
        return this.dataPipes.has(attribute);
    }

    /**
     * Return if a specific attribute has a dot in the {@link hasDot} map
     * @param attribute the attribute type to check
     */
    attributeHasDot(attribute: Enum): boolean {
        return this.hasDot.has(attribute);
    }

    /**
     * Return if a specific attribute has a copy/paste button in the {@link hasCopyPasteButton} map
     * @param attribute the attribute type to check
     */
    attributeHasCopyPaste(attribute: Enum): boolean {
        return this.hasCopyPasteButton.has(attribute);
    }

    /**
     * Return if a specific attribute has textAlignRight in the {@link hasTextAlignRight} map
     * @param attribute the attribute type to check
     */
    attributeHasTextAlignRight(attribute: Enum): boolean {
        return this.hasTextAlignRight.has(attribute);
    }

    /**
     * Return if a specific attribute has hasFloatContent in the {@link hasFloatContent} map
     * @param attribute the attribute type to check
     */
    attributeHasFloatContent(attribute: Enum): boolean {
        return this.hasFloatContent.has(attribute);
    }

    /**
     * Applies the respective data pipe to an attribute and returns the result value
     * @param attribute the attribute type to which apply the data pipe
     * @param value the attribute value to which apply the data pipe
     */
    applyAttributePipe(attribute: Enum, value: any): string | number {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const pipe = this.dataPipes.get(attribute);
        return pipe(value);
    }

    /**
     * <p>Returns if a cell value has a {@link TemplateRef} or not
     * @see TooltipComponent#getTooltipContent
     * @param cellValue
     */
    cellHasTemplateRef(cellValue: any): boolean {
        return typeof cellValue?.templateRef?.createEmbeddedView === 'function';
    }

    /**
     * <p>Decorates any object with accordion-related properties
     * @param object the object that will be decorated
     */
    embedAccordionProprieties(object: object): object {
        const closeAccordion: Observable<void> = merge(
                this.accordionStatusChange.pipe(
                        // Filter with only the event we need
                        filter((isOpen) => !isOpen),
                        // Disregard any value, we only want the event emission
                        map((): void => undefined)
                ),
                this.tableService.openEvent.pipe(
                        // Filter with only the event we need
                        filter((line) => line !== this),
                        // Disregard any value, we only want the event emission
                        map((): void => undefined)
                )
        );

        const openAccordion = (): void => {
            if (!this.line.isOpen) {
                this.toggleAccordion();
            }
        };

        const closeFullLineTooltip = (): void => this.closeFullLineTooltip();

        return {
            ...object,
            closeAccordion,
            openAccordion,
            closeFullLineTooltip,
            anchorReference: this.accordionContainer,
            isLineSelected: this.lineIsSelected
        };
    }

    /**
     * Manually closes the full line tooltip
     */
    closeFullLineTooltip(): void {
        this.closeFullLineTooltipEmitter.emit();
    }

    /**
     * Manually opens the full line tooltip
     */
    openFullLineTooltip(): void {
        this.openFullLineTooltipEmitter.emit();
    }

    disableFullLineTooltip(): boolean {
        return !this.expandableLine || this.disableAccordion;
    }

    /**
     * Manages the accordion toggle between opened and closed
     */
    toggleAccordion(): void {
        if (!this.disableAccordion) {
            if (this.expandableLine) {
                this.line.isOpen = !this.line.isOpen;

                if (this.line.isOpen) {
                    this.subscription = this.scrollEvent.subscribe(() => {
                        this.setLineAsHeaderClass();
                    });
                } else {
                    this.subscription.unsubscribe();
                    this.line.accordionData = null;
                    this.lineAsHeaderClass = 'table-line';
                }

                this.openFullLineTooltip();

                this.accordionStatusChange.emit(this.line.isOpen);
            }
        }
    }

    /**
     * Return the data-cy for each line with the correspondent key
     */
    getDataCyTableLine(key: string): string {
        return `table-line-data-${key}`;
    }

    /**
     * Sets {@link applyMouseHover} and emit {@link isMouseHoverLine} line when mouse over and line is open
     **/
    changeStyleOnMouseHover(event: MouseEvent, line: TableLineModel<Enum, DataObject>): void {
        this.hasLineMouseHoverAndAvailableActions = (event.type === 'mouseover' || event.type === 'click')
                && this.hasTableLineAvailableActions();
        this.applyMouseHover = (event.type === 'mouseover' || event.type === 'click') && line.isOpen;
        this.isMouseHoverLine.emit(this.applyMouseHover);
        this.isMouseLeaveLine.emit(this.applyMouseHover);
    }

    emitAvailableAction(action: TableAvailableActionsEnum, identifier: string): void {
        this.tableService.setAvailableActionValue({identifier, action});
    }

    hasTableLineAvailableActions(): boolean {
        return this.lineAvailableActions?.length > 0;
    }

    showColumn(key: string): boolean {
        return !this.excludedColumns.map((column) => column.toString())
                .includes(key);
    }

    /**
     * Sets the line as header class according to the {@link lineElement} offsetTop
     */
    private setLineAsHeaderClass(): void {
        // The height of the line when fully displayed, minus half the diference between full-height and reduced-height (48px - 16px/2)
        const fullHeightAdjusted = 40;
        // The remainder of the difference between full-height and reduced-height (16px/2)
        const triggerValue = 8;
        // The corrected offsetTop value taking in to consideration the line height
        const correctedOffsetTop = this.lineElement.nativeElement.offsetTop -
                (this.lineElement.nativeElement.offsetHeight - fullHeightAdjusted);

        if (correctedOffsetTop > triggerValue) {
            this.lineAsHeaderClass = 'table-line reduced';
        } else {
            this.lineAsHeaderClass = 'table-line';
        }
    }
}
